import { useQuery, useQueryClient } from '@tanstack/react-query';
import API_CONFIG from '../config/api.config';
import AuthService from '../services/api/auth.service';

// API functions separated from hooks for better testability and reuse
export const vehicleApi = {
  getVehicles: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_VEHICLES}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch vehicles data');
    }

    return response.json();
  },

  getModelAssociations: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_MODEL_ASSOCIATIONS}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch model associations');
    }

    return response.json();
  },

  getModelEngines: async (modelId) => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stoc/async/model-engines/${modelId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch model engines');
    }

    // Expect engine_type_id to be present per engine; pass through as-is
    return response.json();
  },

  getBodyTypes: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_VEHICLE_BODY_TYPES}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch body types');
    }

    return response.json();
  },

  getBoltPatterns: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stoc/async/bolt-patterns`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch bolt patterns');
    }

    const data = await response.json();
    return (data.bolt_patterns || []).map(bp => ({
      value: bp.id.toString(),
      label: bp.value
    }));
  },
};

// React Query hooks
export function useVehicles(options = {}) {
  return useQuery({
    queryKey: ['vehicles'],
    queryFn: vehicleApi.getVehicles,
    ...options
  });
}

export function useModelAssociations(options = {}) {
  return useQuery({
    queryKey: ['modelAssociations'],
    queryFn: vehicleApi.getModelAssociations,
    ...options
  });
}

export function useModelEngines(modelId, options = {}) {
  return useQuery({
    queryKey: ['modelEngines', modelId],
    queryFn: () => modelId ? vehicleApi.getModelEngines(modelId) : null,
    // Skip the query if no modelId is provided
    enabled: !!modelId,
    ...options
  });
}

export function useVehicleBodyTypes(options = {}) {
  return useQuery({
    queryKey: ['vehicleBodyTypes'],
    queryFn: vehicleApi.getBodyTypes,
    ...options
  });
}

export function useBoltPatterns(options = {}) {
  return useQuery({
    queryKey: ['boltPatterns'],
    queryFn: vehicleApi.getBoltPatterns,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options
  });
}

// Helper function to manually invalidate cache
export function useInvalidateVehicleQueries() {
  const queryClient = useQueryClient();
  
  return {
    invalidateVehicles: () => queryClient.invalidateQueries({ queryKey: ['vehicles'] }),
    invalidateModelAssociations: () => queryClient.invalidateQueries({ queryKey: ['modelAssociations'] }),
    invalidateModelEngines: (modelId) => queryClient.invalidateQueries({ 
      queryKey: modelId ? ['modelEngines', modelId] : ['modelEngines'] 
    }),
    invalidateAll: () => queryClient.invalidateQueries({ 
      queryKey: [{ prefix: 'vehicles' }, { prefix: 'modelAssociations' }, { prefix: 'modelEngines' }] 
    })
  };
} 